import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { toast } from 'sonner';

export interface DashboardWidget {
  id: string;
  name: string;
  component: 'revenue' | 'hours' | 'clients' | 'projects' | 'tasks' | 'followups' | 'calendar' | 'deadlines' | 'progress';
  visible: boolean;
  order: number;
  category: 'stat' | 'widget';
}

export interface DashboardPreset {
  id: string;
  name: string;
  description?: string;
  widgets: DashboardWidget[];
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface DashboardCustomizationContextType {
  widgets: DashboardWidget[];
  presets: DashboardPreset[];
  currentPreset: DashboardPreset | null;
  isLoading: boolean;
  updateWidgetVisibility: (id: string, visible: boolean) => void;
  reorderWidgets: (reorderedWidgets: DashboardWidget[]) => void;
  resetToDefault: () => void;
  savePreset: (name: string, description?: string, setAsDefault?: boolean) => Promise<void>;
  loadPreset: (presetId: string) => Promise<void>;
  deletePreset: (presetId: string) => Promise<void>;
  refreshPresets: () => Promise<void>;
}

const DashboardCustomizationContext = createContext<DashboardCustomizationContextType | undefined>(undefined);

const defaultWidgets: DashboardWidget[] = [
  { id: 'revenue', name: 'Monthly Revenue', component: 'revenue', visible: true, order: 0, category: 'stat' },
  { id: 'hours', name: 'Hours Tracked', component: 'hours', visible: true, order: 1, category: 'stat' },
  { id: 'clients', name: 'Active Customers', component: 'clients', visible: true, order: 2, category: 'stat' },
  { id: 'projects', name: 'Completed Projects', component: 'projects', visible: true, order: 3, category: 'stat' },
  { id: 'tasks', name: "Today's Tasks", component: 'tasks', visible: true, order: 4, category: 'widget' },
  { id: 'followups', name: 'Client Follow-ups', component: 'followups', visible: true, order: 5, category: 'widget' },
  { id: 'calendar', name: 'Calendar', component: 'calendar', visible: true, order: 6, category: 'widget' },
  { id: 'deadlines', name: 'Upcoming Deadlines', component: 'deadlines', visible: true, order: 7, category: 'widget' },
  { id: 'progress', name: 'Project Progress', component: 'progress', visible: true, order: 8, category: 'widget' },
];

export const DashboardCustomizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [widgets, setWidgets] = useState<DashboardWidget[]>(defaultWidgets);
  const [presets, setPresets] = useState<DashboardPreset[]>([]);
  const [currentPreset, setCurrentPreset] = useState<DashboardPreset | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load presets and widgets from Supabase
  const refreshPresets = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('dashboard_presets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const loadedPresets = data?.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description || undefined,
        widgets: (p.widgets as any) as DashboardWidget[],
        is_default: p.is_default || false,
        created_at: p.created_at,
        updated_at: p.updated_at,
      })) || [];

      setPresets(loadedPresets);

      // Load default preset or most recent
      const defaultPreset = loadedPresets.find(p => p.is_default);
      if (defaultPreset) {
        setWidgets(defaultPreset.widgets);
        setCurrentPreset(defaultPreset);
      } else if (loadedPresets.length > 0) {
        setWidgets(loadedPresets[0].widgets);
        setCurrentPreset(loadedPresets[0]);
      } else {
        // No presets, use default widgets
        setWidgets(defaultWidgets);
        setCurrentPreset(null);
      }
    } catch (error) {
      console.error('Error loading presets:', error);
      toast.error('Failed to load dashboard presets');
      setWidgets(defaultWidgets);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      refreshPresets();
    } else {
      // Load from localStorage for logged out users
      const saved = localStorage.getItem('dashboardWidgets');
      if (saved) {
        try {
          setWidgets(JSON.parse(saved));
        } catch (e) {
          console.error('Failed to parse saved widgets:', e);
          setWidgets(defaultWidgets);
        }
      }
      setIsLoading(false);
    }
  }, [user]);

  // Save to localStorage for logged out users
  useEffect(() => {
    if (!user) {
      localStorage.setItem('dashboardWidgets', JSON.stringify(widgets));
    }
  }, [widgets, user]);

  const updateWidgetVisibility = (id: string, visible: boolean) => {
    setWidgets(prev => 
      prev.map(widget => 
        widget.id === id ? { ...widget, visible } : widget
      )
    );
  };

  const reorderWidgets = (reorderedWidgets: DashboardWidget[]) => {
    const updated = reorderedWidgets.map((widget, index) => ({
      ...widget,
      order: index
    }));
    setWidgets(updated);
  };

  const resetToDefault = () => {
    setWidgets(defaultWidgets);
    setCurrentPreset(null);
    if (!user) {
      localStorage.removeItem('dashboardWidgets');
    }
  };

  const savePreset = async (name: string, description?: string, setAsDefault: boolean = false) => {
    if (!user) {
      toast.error('You must be logged in to save presets');
      return;
    }

    try {
      // If setting as default, unset other defaults first
      if (setAsDefault) {
        await supabase
          .from('dashboard_presets')
          .update({ is_default: false })
          .eq('user_id', user.id);
      }

      const { data, error } = await supabase
        .from('dashboard_presets')
        .insert([{
          user_id: user.id,
          name,
          description: description || null,
          widgets: widgets as any,
          is_default: setAsDefault,
        }])
        .select()
        .single();

      if (error) throw error;

      toast.success(`Preset "${name}" saved successfully`);
      await refreshPresets();
    } catch (error) {
      console.error('Error saving preset:', error);
      toast.error('Failed to save preset');
    }
  };

  const loadPreset = async (presetId: string) => {
    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      setWidgets(preset.widgets);
      setCurrentPreset(preset);
      toast.success(`Loaded preset: ${preset.name}`);
    }
  };

  const deletePreset = async (presetId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('dashboard_presets')
        .delete()
        .eq('id', presetId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Preset deleted');
      
      // If deleted current preset, reset to default
      if (currentPreset?.id === presetId) {
        resetToDefault();
      }
      
      await refreshPresets();
    } catch (error) {
      console.error('Error deleting preset:', error);
      toast.error('Failed to delete preset');
    }
  };

  return (
    <DashboardCustomizationContext.Provider 
      value={{ 
        widgets,
        presets,
        currentPreset,
        isLoading,
        updateWidgetVisibility, 
        reorderWidgets, 
        resetToDefault,
        savePreset,
        loadPreset,
        deletePreset,
        refreshPresets,
      }}
    >
      {children}
    </DashboardCustomizationContext.Provider>
  );
};

export const useDashboardCustomization = () => {
  const context = useContext(DashboardCustomizationContext);
  if (!context) {
    throw new Error('useDashboardCustomization must be used within DashboardCustomizationProvider');
  }
  return context;
};
